import {
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import TaskAssigneePicker from './TaskAssigneePicker'
import type { TaskFormFieldDefinition } from './modules/types'

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

export type TaskMetaHandlers = {
  priority?: string
  dueAt?: string
  assignedTo?: number | ''
  assigneeName?: string | null
  onChange?: (patch: {
    priority?: string
    dueAt?: string
    assignedTo?: number | ''
    assigneeName?: string
  }) => void
  isReadOnly?: boolean
}

type Props = {
  field: TaskFormFieldDefinition
  formData: Record<string, unknown>
  onFormDataChange: (key: string, value: unknown) => void
  ticketPropertiesProps?: Record<string, unknown>
}

function toDatetimeLocalValue(iso?: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function fromDatetimeLocalValue(value: string): string | null {
  if (!value.trim()) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

function resolveSharedFieldKeys(field: TaskFormFieldDefinition): string[] {
  const fromConfig = field.config?.shared_base_fields
  if (Array.isArray(fromConfig) && fromConfig.length > 0) {
    return fromConfig.map(String)
  }
  return ['assignee', 'due_at', 'priority', 'remarks']
}

export default function TaskModuleSharedBaseFields({
  field,
  formData,
  onFormDataChange,
  ticketPropertiesProps,
}: Props) {
  const handlers = ticketPropertiesProps?.taskMetaHandlers as TaskMetaHandlers | undefined
  const isReadOnly = handlers?.isReadOnly ?? false
  const sharedKeys = resolveSharedFieldKeys(field)
  const remarks = String(formData.remarks ?? '')

  return (
    <VStack align="stretch" spacing={3} mb={4}>
      {sharedKeys.includes('assignee') ? (
        <TaskAssigneePicker
          assignedTo={handlers?.assignedTo ?? ''}
          assigneeName={handlers?.assigneeName}
          onChange={(userId, displayName) => {
            handlers?.onChange?.({ assignedTo: userId, assigneeName: displayName })
          }}
          isDisabled={isReadOnly}
        />
      ) : null}

      {sharedKeys.includes('due_at') || sharedKeys.includes('priority') ? (
        <HStack align="flex-end" spacing={3} flexWrap="wrap">
          {sharedKeys.includes('priority') ? (
            <FormControl flex="1" minW="140px">
              <FormLabel fontSize="xs">Priority</FormLabel>
              <Select
                size="sm"
                value={(handlers?.priority ?? 'medium').toLowerCase()}
                onChange={(e) => handlers?.onChange?.({ priority: e.target.value })}
                isDisabled={isReadOnly}
              >
                {PRIORITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          ) : null}
          {sharedKeys.includes('due_at') ? (
            <FormControl flex="1" minW="180px">
              <FormLabel fontSize="xs">Due date</FormLabel>
              <Input
                size="sm"
                type="datetime-local"
                value={toDatetimeLocalValue(handlers?.dueAt)}
                onChange={(e) => handlers?.onChange?.({ dueAt: fromDatetimeLocalValue(e.target.value) ?? '' })}
                isDisabled={isReadOnly}
              />
            </FormControl>
          ) : null}
        </HStack>
      ) : null}

      {sharedKeys.includes('remarks') ? (
        <FormControl>
          <FormLabel fontSize="xs">Remarks</FormLabel>
          <Textarea
            size="sm"
            rows={3}
            value={remarks}
            onChange={(e) => onFormDataChange('remarks', e.target.value)}
            placeholder="Notes for this task"
            isDisabled={isReadOnly}
          />
        </FormControl>
      ) : null}
    </VStack>
  )
}
